import { Center, type MantineColor, RingProgress } from "@mantine/core";
import type { PropsWithChildren } from "react";
import { useEffect, useState } from "react";
import classes from "./avatar-styles.module.css";
import { RippleAnimation } from "./RippleAnimation";

const PLAY_RIPPLE_INTERVAL = 30;

interface TimerProps {
    duration?: number;
    initialPercentage?: number;
}

interface RingProps {
    scaleRatio: number;
    size: number;
    thickness: number;
}

interface NotificationTimerRingProps {
    timer: TimerProps;
    variantColor: MantineColor;
    withRippleAnimation?: boolean;
    ring: RingProps;
}

export function NotificationTimerRing({
    variantColor,
    timer: { initialPercentage = 0, duration = 0 },
    withRippleAnimation,
    ring: { size: ringSize, thickness, scaleRatio },
    children,
}: PropsWithChildren<NotificationTimerRingProps>) {
    const [ringValue, setRingValue] = useState(initialPercentage);
    const showRing = duration > 0;
    const ringColor = showRing ? "var(--mantine-color-dark-9)" : "transparent";

    useEffect(() => {
        if (showRing) {
            setRingValue(100);
        } else {
            setRingValue(initialPercentage);
        }
    }, [initialPercentage, showRing]);

    return (
        <Center
            style={{
                position: "relative",
                width: ringSize,
                height: ringSize,
                minWidth: ringSize,
                minHeight: ringSize,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }}
        >
            {showRing && withRippleAnimation && (
                <RippleAnimation color={variantColor} repeatInterval={PLAY_RIPPLE_INTERVAL} size={ringSize} />
            )}
            <RingProgress
                className={classes.notification_ring}
                roundCaps={showRing}
                rootColor={ringColor}
                size={ringSize}
                thickness={thickness}
                transitionDuration={duration}
                sections={[{ value: ringValue, color: variantColor }]}
                styles={{
                    svg: {
                        display: "block",
                        margin: "auto",
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        transform: `scale(${scaleRatio}, -${scaleRatio}) rotate(45deg)`,
                    },
                    curve: {
                        transitionTimingFunction: "linear",
                        transitionProperty: "stroke-dasharray, stroke-dashoffset",
                    },
                    label: {
                        insetInline: 0,
                        className: classes.notification_ring_label,
                    },
                    root: {
                        width: "100%",
                        height: "100%",
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    },
                }}
                label={children}
            />
        </Center>
    );
}
